import { useEffect, useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import Markdown from "../Markdown.jsx";
import LaunchCard from "../LaunchCard.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { readWiki, subscribeWiki } from "../../lib/wikiStore.js";

// X-Ray › Wiki panel — the reader and the run gate for the wiki pipeline.
//
// The wiki is DURABLE (wikiStore), unlike every sibling tab's window-keyed
// scan: pages survive scope changes and reloads, and a regeneration updates
// them in place rather than piling up new ones. So this panel renders the
// store first and treats the run as maintenance on it — the LaunchCard only
// owns the screen while the wiki is empty.
//
// The store spans every scope but the READER is scope-aware: the current
// scope's pages lead, everything else sits under "other scopes", and the
// header (profile, run line, problems) belongs to THIS scope's latest run.
// Without the partition, regenerating inside an endpoint rewrote what the
// org-level wiki showed — a narrow run must never read as the whole story.
//
// The run is the scoped-scout pipeline (lib/wikiXray): the scout DECIDES the
// corpus (which sessions evidence which pages) instead of inheriting the
// scrubber's newest-N window, which is why this tab renders no window-scan
// vitals and never re-runs on a scrub.

const STAGE_LABEL = {
  scout: "Choosing pages for this scope",
  evidence: "Pulling evidence sessions",
  pages: "Writing pages",
};

const KIND_ORDER = ["workflow", "subsystem", "tool", "practice"];
const EYEBROW = "text-[11px] leading-[1.08] tracking-[0.06em] uppercase font-medium text-[color:var(--eyebrow)]";

const keyOf = (p) => `${p.scopeKey || ""}::${p.slug}`;

// Steer text can carry a 300-id session list — clamp it for the preview
// banner (the full text lives in the sidebar steer editor).
const clipSteer = (s) => {
  const t = String(s || "");
  return t.length > 80 ? `${t.slice(0, 80)}…` : t;
};

function FilterChip({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-[2px] border border-[color:var(--line)] px-[6px] py-[2px] text-[10px] leading-[1.2] uppercase tracking-[0.04em] text-[color:var(--muted)] transition-colors cursor-pointer hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
    >
      {label}
    </button>
  );
}

const WHEN_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const when = (ms) => (Number.isFinite(ms) ? WHEN_FMT.format(new Date(ms)) : "–");

function RunProgress({ run, onAbort }) {
  const p = run.progress || {};
  const label = STAGE_LABEL[p.stage] || "Working";
  const counted = p.total ? ` · ${p.done ?? 0} of ${p.total}` : "";
  return (
    <div className="grid justify-items-start gap-[8px] px-[12px] py-[10px]" aria-busy="true">
      <span className={`${NOTE_CLASS} animate-pulse`}>{label}{counted}…</span>
      <span className={NOTE_CLASS}>
        Selecting evidence and drafting pages. The wiki updates when the run finishes.
      </span>
      <FilterChip label="Stop" onClick={onAbort} />
    </div>
  );
}

function IndexEntry({ page, selected, onSelect, hint }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(keyOf(page))}
      className={[
        "grid w-full min-w-0 gap-[1px] overflow-hidden rounded-[2px] border px-[8px] py-[5px] text-left cursor-pointer transition-colors",
        selected
          ? "border-[color:var(--line-strong)] bg-[color:var(--control-bg)]"
          : "border-transparent hover:border-[color:var(--line)]",
      ].join(" ")}
    >
      <span className="flex min-w-0 items-baseline gap-[6px]">
        <span className="min-w-0 truncate text-[11.5px] leading-[1.3] text-[color:var(--ink)]">{page.title}</span>
        {page.stale ? (
          <span className="ml-auto shrink-0 rounded-[2px] border border-[color:var(--line)] px-[3px] text-[9px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)]">stale</span>
        ) : null}
      </span>
      <span className="min-w-0 truncate text-[10px] text-[color:var(--muted-soft)]">{hint ?? (page.summary || page.slug)}</span>
    </button>
  );
}

function PageIndex({ scoped, others, selected, onSelect }) {
  const groups = useMemo(() => {
    const byKind = new Map();
    for (const p of scoped) {
      const k = KIND_ORDER.includes(p.kind) ? p.kind : "practice";
      if (!byKind.has(k)) byKind.set(k, []);
      byKind.get(k).push(p);
    }
    return KIND_ORDER.filter((k) => byKind.has(k)).map((k) => ({ kind: k, pages: byKind.get(k) }));
  }, [scoped]);
  return (
    <ScrollArea
      className="min-h-0 h-full w-[224px] shrink-0 border-r border-[color:var(--line)]"
      contentClassName="grid min-w-0 content-start gap-[10px] px-[10px] py-[10px]"
      options={{ overflow: { x: "hidden" } }}
    >
      {groups.map((g) => (
        <div key={g.kind} className="grid min-w-0 gap-[3px]">
          <span className={EYEBROW}>{g.kind}s</span>
          {g.pages.map((p) => (
            <IndexEntry key={p.slug} page={p} selected={selected === keyOf(p)} onSelect={onSelect} />
          ))}
        </div>
      ))}
      {others.length ? (
        <div className="grid min-w-0 gap-[3px]">
          <span className={EYEBROW}>Other scopes</span>
          {others.map((p) => (
            <IndexEntry
              key={keyOf(p)}
              page={p}
              selected={selected === keyOf(p)}
              onSelect={onSelect}
              hint={p.scopeText || "another scope"}
            />
          ))}
        </div>
      ) : null}
    </ScrollArea>
  );
}

function PageBody({ page, foreign, resolvePage, onSelectPage, onSelectTrace }) {
  // Both link families route through Markdown's renderLink hook: [[slug]]
  // wikilinks navigate the index, (trace:id) citations open the trace. A
  // wikilink resolves inside ITS OWN page's scope — siblings were planned by
  // the same generation — and an unknown one renders as plain text, because a
  // link to a page that failed to write must not read as navigation.
  const renderLink = ({ wiki, target, href, children, key }) => {
    if (wiki) {
      const hit = resolvePage(page.scopeKey, target);
      if (!hit) return <span key={key}>{children}</span>;
      return (
        <button
          key={key}
          type="button"
          onClick={() => onSelectPage(keyOf(hit))}
          className="cursor-pointer border-b border-[color:var(--line-strong)] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
        >
          {children}
        </button>
      );
    }
    const trace = /^trace:(.+)$/.exec(String(href || ""));
    if (trace) {
      return (
        <button
          key={key}
          type="button"
          onClick={() => onSelectTrace(trace[1])}
          title="Open this trace"
          className="cursor-pointer border-b border-dotted border-[color:var(--line-strong)] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
        >
          {children}
        </button>
      );
    }
    return null;
  };

  return (
    <ScrollArea className="min-h-0 h-full flex-1" contentClassName="grid content-start gap-[10px] px-[14px] py-[10px]">
      <div className="grid gap-[3px]">
        <span className="text-[14px] font-semibold leading-[1.3] text-[color:var(--ink)]">{page.title}</span>
        <span className={NOTE_CLASS}>
          {page.kind} · updated {when(page.updatedAt)} · {page.sessionIds?.length || 0} evidence session{(page.sessionIds?.length || 0) === 1 ? "" : "s"}
          {foreign ? ` · generated for ${page.scopeText || "another scope"}` : ""}
          {page.stale ? " · stale; absent from the latest run for this scope" : ""}
        </span>
        {page.basis ? <span className={NOTE_CLASS}>Basis: {page.basis}</span> : null}
      </div>
      <div className="text-[12.5px] leading-[1.6] text-[color:var(--ink)]">
        <Markdown text={page.markdown} wikiLinks renderLink={renderLink} />
      </div>
      {page.evidence?.length ? (
        <div className="grid justify-items-start gap-[4px] border-t border-[color:var(--line-soft)] pt-[8px]">
          <span className={EYEBROW}>Evidence</span>
          <div className="flex flex-wrap gap-[6px]">
            {page.evidence.map((e) => (
              <button
                key={e.promptId}
                type="button"
                onClick={() => onSelectTrace(e.promptId)}
                title="Open this trace"
                className="cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[6px] py-[2px] font-mono text-[10px] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
              >
                session {String(e.sessionId).slice(0, 8)} →
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {page.log?.length ? (
        <div className="grid gap-[2px] border-t border-[color:var(--line-soft)] pt-[8px]">
          <span className={EYEBROW}>History</span>
          {page.log.slice(-6).map((l, i) => (
            <span key={i} className={NOTE_CLASS}>
              {when(l.at)} · {l.action}{l.note ? ` · ${l.note}` : ""}
            </span>
          ))}
        </div>
      ) : null}
    </ScrollArea>
  );
}

export default function WikiPanel({ run, onRun, onAbort, scopeKey, scopeLabel, steer = "", modelControl = null, onSelectTrace }) {
  const [wiki, setWiki] = useState(() => readWiki());
  useEffect(() => subscribeWiki(() => setWiki(readWiki())), []);

  // A STEERED run's generation is ephemeral (never written to the store —
  // see runWikiGeneration); it rides run.ephemeral and takes over the view
  // as a labeled preview until the steer changes, the next run starts, or a
  // rescope clears it.
  const ephemeral = run?.ephemeral || null;
  const ephemeralPages = useMemo(() => {
    if (!ephemeral) return null;
    const at = ephemeral.meta?.at || null;
    return ephemeral.pages.map((p) => ({
      ...p,
      scopeKey: ephemeral.meta?.scopeKey ?? null,
      scopeText: ephemeral.meta?.scopeText || null,
      stale: false,
      updatedAt: at,
      log: [],
    }));
  }, [ephemeral]);

  const { scoped, others } = useMemo(() => {
    if (ephemeralPages) return { scoped: ephemeralPages, others: [] };
    const scoped = [];
    const others = [];
    for (const p of wiki.pages) ((p.scopeKey || null) === (scopeKey || null) ? scoped : others).push(p);
    return { scoped, others };
  }, [ephemeralPages, wiki.pages, scopeKey]);
  const scopeRun = wiki.runs?.[scopeKey || "org"] || null;

  const [selected, setSelected] = useState(null);
  const pagesForView = ephemeralPages ?? wiki.pages;
  const byKey = useMemo(() => new Map(pagesForView.map((p) => [keyOf(p), p])), [pagesForView]);
  const resolvePage = (pageScopeKey, slug) => byKey.get(`${pageScopeKey || ""}::${slug}`) || null;
  const current = (selected && byKey.get(selected)) || scoped[0] || others[0] || null;

  const steering = Boolean(steer && steer.trim());
  const steerNote = steering
    ? "steer active — the next run reads only steered sessions and is NOT saved to the wiki"
    : null;

  if (run.status === "running") return <RunProgress run={run} onAbort={onAbort} />;

  if (!pagesForView.length) {
    if (run.status === "error") {
      return (
        <div className="grid justify-items-start gap-[8px] px-[12px] py-[10px]">
          <span className="font-mono text-[11px] leading-[1.4] text-[color:var(--red,#c0392b)] break-words">
            {run.stage ? `${run.stage}: ` : ""}{run.error}
          </span>
          <div className="flex items-center gap-[8px]">
            <FilterChip label="Retry" onClick={onRun} />
            {modelControl}
          </div>
        </div>
      );
    }
    return (
      <div className="grid gap-[6px] px-[12px] py-[10px]">
        <LaunchCard
          eyebrow="Wiki"
          description={`Build a cited wiki of recurring workflows, subsystems, and tool practices in ${scopeLabel || "this scope"}. Regenerate it to update the existing pages.`}
          controls={modelControl ? (
            <div className="flex items-center gap-[8px] min-w-0">
              <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">Model</span>
              {modelControl}
            </div>
          ) : null}
          ctaLabel={steering ? "Generate steered preview" : "Generate wiki"}
          onLaunch={onRun}
        />
        {steerNote ? <span className={NOTE_CLASS}>{steerNote}</span> : null}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="grid gap-[6px] border-b border-[color:var(--line)] px-[12px] py-[8px]">
        {(ephemeral?.profile ?? scopeRun?.profile) ? (
          <span className="text-[12px] leading-[1.5] text-[color:var(--muted)]">{ephemeral?.profile ?? scopeRun?.profile}</span>
        ) : null}
        <div className="flex flex-wrap items-center gap-[8px]">
          <FilterChip
            label={run.status === "error" ? "Retry"
              : steering ? "Generate steered preview"
              : scopeRun ? "Regenerate for this scope" : "Generate for this scope"}
            onClick={onRun}
          />
          {modelControl}
          {ephemeral ? (
            <span className={NOTE_CLASS}>
              <span className="text-[color:var(--green)]">steered preview</span>
              {" · not saved to the wiki · sessions matching "}
              <span className="font-mono text-[color:var(--ink)]">{clipSteer(ephemeral.meta?.steered)}</span>
              {" only · clear the steer and regenerate to update the durable pages"}
            </span>
          ) : scopeRun ? (
            <span className={NOTE_CLASS}>
              Last run {when(scopeRun.at)}
              {scopeRun.scopeText ? ` · ${scopeRun.scopeText}` : ""}
              {` · ${scopeRun.counts?.created ?? 0} new / ${scopeRun.counts?.updated ?? 0} updated / ${scopeRun.counts?.staled ?? 0} marked stale`}
            </span>
          ) : (
            <span className={NOTE_CLASS}>No wiki for this scope. Showing pages from other scopes.</span>
          )}
        </div>
        {steerNote && !ephemeral ? <span className={NOTE_CLASS}>{steerNote}</span> : null}
        {run.status === "error" ? (
          <span className="font-mono text-[11px] leading-[1.4] text-[color:var(--red,#c0392b)] break-words">
            {run.stage ? `${run.stage}: ` : ""}{run.error}
          </span>
        ) : null}
        {(ephemeral ? ephemeral.problems : scopeRun?.problems)?.length ? (
          <details>
            <summary className={`${NOTE_CLASS} cursor-pointer`}>
              Unresolved items · {(ephemeral ? ephemeral.problems : scopeRun.problems).length}
            </summary>
            <div className="grid gap-[2px] pt-[4px]">
              {(ephemeral ? ephemeral.problems : scopeRun.problems).map((p, i) => (
                <span key={i} className={`${NOTE_CLASS} break-words`}>{p}</span>
              ))}
            </div>
          </details>
        ) : null}
      </div>
      <div className="flex min-h-0 flex-1">
        <PageIndex scoped={scoped} others={others} selected={current ? keyOf(current) : null} onSelect={setSelected} />
        {current ? (
          <PageBody
            page={current}
            foreign={(current.scopeKey || null) !== (scopeKey || null)}
            resolvePage={resolvePage}
            onSelectPage={setSelected}
            onSelectTrace={onSelectTrace}
          />
        ) : null}
      </div>
    </div>
  );
}
